import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HardDrive, Loader2, Search, Trash2, Copy, Save } from 'lucide-react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import api from '../../services/api';
import { useToast } from '../../hooks/use-toast';

type CampoDias = 'diasImagens' | 'diasVideos' | 'diasAudios' | 'diasDocumentos';
type TipoJob = 'simulacao' | 'limpeza' | 'deduplicacao';

interface Configuracao {
  empresaId: string;
  ativo: boolean;
  diasImagens: number | null;
  diasVideos: number | null;
  diasAudios: number | null;
  diasDocumentos: number | null;
  horario: string;
  ultimaExecucaoEm?: string | null;
  ultimaExecucaoStatus?: 'SUCESSO' | 'ERRO' | null;
  ultimaExecucaoMensagem?: string | null;
  ultimosArquivosRemovidos?: number | null;
  ultimosBytesLiberados?: number | null;
}

interface StatsPasta {
  dias: number | null;
  arquivos: number;
  bytes: number;
  arquivosExpirados: number;
  bytesExpirados: number;
}

interface Job {
  tipo: TipoJob;
  status: 'executando' | 'concluido' | 'erro';
  progresso: string | null;
  iniciadoEm: string;
  finalizadoEm: string | null;
  erro: string | null;
  resultado: any;
}

interface ItemEmpresa {
  empresa: { id: string; nome: string; ativo: boolean };
  configuracao: Configuracao;
  job: Job | null;
}

const TIPOS: { campo: CampoDias; pasta: string; label: string }[] = [
  { campo: 'diasImagens', pasta: 'imagens', label: 'Imagens e figurinhas' },
  { campo: 'diasVideos', pasta: 'videos', label: 'Vídeos' },
  { campo: 'diasAudios', pasta: 'audios', label: 'Áudios' },
  { campo: 'diasDocumentos', pasta: 'documentos', label: 'Documentos' },
];

const NOME_JOB: Record<TipoJob, string> = {
  simulacao: 'Simulação',
  limpeza: 'Limpeza',
  deduplicacao: 'Unificação de duplicados',
};

const extractError = (e: any) => e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Erro inesperado';

export const formatarBytes = (bytes?: number | null) => {
  if (!bytes) return '0 B';
  const unidades = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), unidades.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(i >= 3 ? 2 : i === 0 ? 0 : 1).replace('.', ',')} ${unidades[i]}`;
};

const formatarNumero = (n?: number | null) => (n ?? 0).toLocaleString('pt-BR');

const formatarDataHora = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—');

const resumoPrazos = (c: Configuracao) => {
  const definidos = TIPOS.filter((t) => c[t.campo]);
  if (definidos.length === 0) return 'Manter tudo';
  return definidos.map((t) => `${t.label.split(' ')[0]} ${c[t.campo]}d`).join(' · ');
};

// Formulário guarda os dias como texto para permitir campo vazio ("manter sempre")
type FormState = { ativo: boolean; horario: string } & Record<CampoDias, string>;

const paraForm = (c: Configuracao): FormState => ({
  ativo: c.ativo,
  horario: c.horario || '03:30',
  diasImagens: c.diasImagens ? String(c.diasImagens) : '',
  diasVideos: c.diasVideos ? String(c.diasVideos) : '',
  diasAudios: c.diasAudios ? String(c.diasAudios) : '',
  diasDocumentos: c.diasDocumentos ? String(c.diasDocumentos) : '',
});

const paraPayload = (f: FormState) => ({
  ativo: f.ativo,
  horario: f.horario,
  diasImagens: f.diasImagens.trim() === '' ? null : Number(f.diasImagens),
  diasVideos: f.diasVideos.trim() === '' ? null : Number(f.diasVideos),
  diasAudios: f.diasAudios.trim() === '' ? null : Number(f.diasAudios),
  diasDocumentos: f.diasDocumentos.trim() === '' ? null : Number(f.diasDocumentos),
});

const ResultadoJob: React.FC<{ job: Job }> = ({ job }) => {
  if (job.status === 'erro') {
    return <p className="text-sm text-red-600 dark:text-red-400">{NOME_JOB[job.tipo]} falhou: {job.erro}</p>;
  }
  if (job.status !== 'concluido' || !job.resultado) return null;
  const r = job.resultado;

  if (job.tipo === 'deduplicacao') {
    return (
      <p className="text-sm text-gray-700 dark:text-gray-200">
        {formatarNumero(r.arquivosAnalisados)} arquivos analisados. {formatarNumero(r.arquivosUnificados)} cópias idênticas unificadas,{' '}
        <strong>{formatarBytes(r.bytesRecuperados)}</strong> recuperados.
        {r.falhas > 0 && <span className="text-amber-600"> {formatarNumero(r.falhas)} arquivo(s) não puderam ser processados.</span>}
      </p>
    );
  }

  const porPasta: Record<string, StatsPasta> = r.porPasta || {};
  const ehSimulacao = job.tipo === 'simulacao';
  const totalExpirado = Object.values(porPasta).reduce((s, p) => s + (p.bytesExpirados || 0), 0);

  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-700 dark:text-gray-200">
        {ehSimulacao ? (
          <>Com esses prazos seriam removidos <strong>{formatarBytes(totalExpirado)}</strong> (estimativa).</>
        ) : (
          <>
            {formatarNumero(r.arquivosRemovidos)} arquivo(s) removidos, <strong>{formatarBytes(r.bytesLiberados)}</strong> liberados,{' '}
            {formatarNumero(r.mensagensMarcadas)} mensagem(ns) marcadas como mídia expirada.
          </>
        )}
        {r.arquivosProtegidos > 0 && (
          <> {formatarNumero(r.arquivosProtegidos)} arquivo(s) preservados por estarem em agendamentos ativos.</>
        )}
      </p>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tipo</TableHead>
              <TableHead>Prazo</TableHead>
              <TableHead className="text-right">Arquivos</TableHead>
              <TableHead className="text-right">Tamanho</TableHead>
              <TableHead className="text-right">{ehSimulacao ? 'A remover' : 'Removido'}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {TIPOS.map((t) => {
              const p = porPasta[t.pasta];
              if (!p) return null;
              return (
                <TableRow key={t.pasta}>
                  <TableCell className="dark:text-white">{t.label}</TableCell>
                  <TableCell className="dark:text-white">{p.dias ? `${p.dias} dias` : 'Manter sempre'}</TableCell>
                  <TableCell className="text-right dark:text-white">{formatarNumero(p.arquivos)}</TableCell>
                  <TableCell className="text-right dark:text-white">{formatarBytes(p.bytes)}</TableCell>
                  <TableCell className="text-right dark:text-white">
                    {formatarNumero(p.arquivosExpirados)} · {formatarBytes(p.bytesExpirados)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const RetencaoMidiasConfig: React.FC = () => {
  const { toast } = useToast();
  const [carregando, setCarregando] = useState(true);
  const [itens, setItens] = useState<ItemEmpresa[]>([]);
  const [filtro, setFiltro] = useState('');
  const [selecionadaId, setSelecionadaId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [confirmar, setConfirmar] = useState<null | 'limpeza' | 'deduplicacao'>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const selecionada = useMemo(() => itens.find((i) => i.empresa.id === selecionadaId) || null, [itens, selecionadaId]);
  const jobAtual = selecionada?.job || null;
  const executando = jobAtual?.status === 'executando';

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get('/superadmin/retencao-midias');
      setItens(data.empresas || []);
    } catch (e) {
      toast({ title: 'Erro', description: extractError(e), variant: 'destructive' });
    } finally {
      setCarregando(false);
    }
  }, [toast]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Ao trocar de empresa, carrega o formulário com a configuração salva
  useEffect(() => {
    if (selecionada) setForm(paraForm(selecionada.configuracao));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecionadaId]);

  const atualizarItem = (empresaId: string, patch: Partial<ItemEmpresa>) => {
    setItens((prev) => prev.map((i) => (i.empresa.id === empresaId ? { ...i, ...patch } : i)));
  };

  // Acompanha a tarefa em andamento da empresa selecionada
  useEffect(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (!selecionadaId || !executando) return;

    pollingRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/superadmin/retencao-midias/${selecionadaId}/status`);
        atualizarItem(selecionadaId, { job: data.job, configuracao: data.configuracao });
        if (data.job?.status !== 'executando') {
          if (data.job?.status === 'concluido') {
            toast({ title: `${NOME_JOB[data.job.tipo as TipoJob]} concluída` });
          } else if (data.job?.status === 'erro') {
            toast({ title: `${NOME_JOB[data.job.tipo as TipoJob]} falhou`, description: data.job.erro, variant: 'destructive' });
          }
        }
      } catch {
        // tenta de novo no próximo ciclo
      }
    }, 3000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecionadaId, executando]);

  const handleSalvar = async () => {
    if (!selecionada || !form) return;
    setSalvando(true);
    try {
      const { data } = await api.put(`/superadmin/retencao-midias/${selecionada.empresa.id}`, paraPayload(form));
      atualizarItem(selecionada.empresa.id, { configuracao: data.configuracao });
      toast({ title: 'Sucesso', description: 'Retenção de mídias salva.' });
    } catch (e) {
      toast({ title: 'Erro', description: extractError(e), variant: 'destructive' });
    } finally {
      setSalvando(false);
    }
  };

  const iniciar = async (tipo: TipoJob) => {
    if (!selecionada || !form) return;
    const empresaId = selecionada.empresa.id;
    const rota = { simulacao: 'simular', limpeza: 'executar', deduplicacao: 'deduplicar' }[tipo];
    try {
      const corpo = tipo === 'simulacao' ? paraPayload(form) : {};
      const { data } = await api.post(`/superadmin/retencao-midias/${empresaId}/${rota}`, corpo);
      atualizarItem(empresaId, { job: data.job });
    } catch (e) {
      toast({ title: 'Erro', description: extractError(e), variant: 'destructive' });
    }
  };

  const alterado = !!(selecionada && form && JSON.stringify(paraForm(selecionada.configuracao)) !== JSON.stringify(form));
  const temPrazoSalvo = !!selecionada && TIPOS.some((t) => selecionada.configuracao[t.campo]);

  const itensFiltrados = itens.filter((i) => i.empresa.nome.toLowerCase().includes(filtro.trim().toLowerCase()));

  if (carregando) {
    return <div className="p-6 text-gray-500 dark:text-gray-400">Carregando...</div>;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <HardDrive className="h-5 w-5 text-red-600" />
        <h1 className="text-2xl font-bold dark:text-white">Retenção de mídias</h1>
      </div>
      <p className="text-gray-500 dark:text-gray-400 mb-6">
        Defina por quantos dias cada empresa guarda as mídias das conversas. Arquivos mais antigos que o prazo são apagados
        do servidor todos os dias no horário escolhido, e o chat passa a mostrar "mídia expirada" no lugar deles. Respostas
        rápidas, fotos de perfil, logos e mídias de agendamentos ainda ativos nunca são apagados.
      </p>

      <Card className="mb-6 p-4 dark:bg-slate-800">
        <div className="relative mb-3 max-w-xs">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Buscar empresa"
            className="pl-8 dark:bg-slate-700 dark:text-white"
          />
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Empresa</TableHead>
                <TableHead>Limpeza automática</TableHead>
                <TableHead>Prazos</TableHead>
                <TableHead>Última limpeza</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {itensFiltrados.map((item) => {
                const c = item.configuracao;
                const ativa = item.empresa.id === selecionadaId;
                return (
                  <TableRow
                    key={item.empresa.id}
                    onClick={() => setSelecionadaId(item.empresa.id)}
                    className={`cursor-pointer ${ativa ? 'bg-red-50 dark:bg-slate-700' : ''}`}
                  >
                    <TableCell className="font-medium dark:text-white">
                      {item.empresa.nome}
                      {!item.empresa.ativo && <span className="ml-2 text-xs text-gray-400">(inativa)</span>}
                    </TableCell>
                    <TableCell>
                      {c.ativo ? <Badge>Ativa · {c.horario}</Badge> : <Badge variant="secondary">Desligada</Badge>}
                    </TableCell>
                    <TableCell className="text-sm dark:text-white">{resumoPrazos(c)}</TableCell>
                    <TableCell className="text-sm dark:text-white">
                      {c.ultimaExecucaoEm ? (
                        <>
                          {formatarDataHora(c.ultimaExecucaoEm)}
                          {c.ultimaExecucaoStatus === 'ERRO' ? (
                            <span className="ml-1 text-red-600">(erro)</span>
                          ) : (
                            <span className="ml-1 text-gray-500">· {formatarBytes(c.ultimosBytesLiberados)}</span>
                          )}
                        </>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {itensFiltrados.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-gray-500">Nenhuma empresa encontrada.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {!selecionada || !form ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Selecione uma empresa na lista para configurar.</p>
      ) : (
        <Card className="p-4 dark:bg-slate-800">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold dark:text-white">{selecionada.empresa.nome}</h2>
            <Button onClick={handleSalvar} disabled={salvando || !alterado}>
              {salvando ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              Salvar
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-6 mb-4">
            <div className="flex items-center gap-2">
              <Switch checked={form.ativo} onCheckedChange={(v) => setForm({ ...form, ativo: v })} id="retencaoAtivo" />
              <Label htmlFor="retencaoAtivo" className="dark:text-white">Limpeza automática diária</Label>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="retencaoHorario" className="dark:text-white">Horário</Label>
              <Input
                id="retencaoHorario"
                type="time"
                value={form.horario}
                onChange={(e) => setForm({ ...form, horario: e.target.value })}
                className="w-32 dark:bg-slate-700 dark:text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2 max-w-2xl">
            {TIPOS.map((t) => (
              <div key={t.campo}>
                <Label htmlFor={t.campo} className="dark:text-white">{t.label} (dias)</Label>
                <Input
                  id={t.campo}
                  type="number"
                  min={1}
                  max={3650}
                  value={form[t.campo]}
                  onChange={(e) => setForm({ ...form, [t.campo]: e.target.value })}
                  placeholder="Manter sempre"
                  className="mt-1 dark:bg-slate-700 dark:text-white"
                />
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Deixe vazio para nunca apagar aquele tipo. O prazo conta a partir do dia em que a mídia foi salva.
          </p>

          <div className="flex flex-wrap gap-2 mb-4">
            <Button variant="outline" onClick={() => iniciar('simulacao')} disabled={executando} className="dark:text-white">
              <Search className="h-4 w-4 mr-1" /> Simular
            </Button>
            <Button
              variant="destructive"
              onClick={() => setConfirmar('limpeza')}
              disabled={executando || !temPrazoSalvo || alterado}
              title={alterado ? 'Salve as alterações antes de limpar' : !temPrazoSalvo ? 'Defina e salve um prazo primeiro' : undefined}
            >
              <Trash2 className="h-4 w-4 mr-1" /> Limpar agora
            </Button>
            <Button variant="outline" onClick={() => setConfirmar('deduplicacao')} disabled={executando} className="dark:text-white">
              <Copy className="h-4 w-4 mr-1" /> Unificar duplicados
            </Button>
          </div>

          {jobAtual && (
            <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3 mb-4">
              <div className="flex items-center gap-2 mb-2 text-sm font-medium dark:text-white">
                {executando && <Loader2 className="h-4 w-4 animate-spin" />}
                {NOME_JOB[jobAtual.tipo]} · {executando ? jobAtual.progresso || 'Em andamento' : `iniciada em ${formatarDataHora(jobAtual.iniciadoEm)}`}
              </div>
              <ResultadoJob job={jobAtual} />
            </div>
          )}

          <div className="text-sm text-gray-600 dark:text-gray-300">
            <strong>Última limpeza:</strong> {formatarDataHora(selecionada.configuracao.ultimaExecucaoEm)}
            {selecionada.configuracao.ultimaExecucaoStatus === 'SUCESSO' && (
              <> · {formatarNumero(selecionada.configuracao.ultimosArquivosRemovidos)} arquivo(s), {formatarBytes(selecionada.configuracao.ultimosBytesLiberados)} liberados</>
            )}
            {selecionada.configuracao.ultimaExecucaoStatus === 'ERRO' && (
              <span className="text-red-600"> · erro: {selecionada.configuracao.ultimaExecucaoMensagem}</span>
            )}
            {selecionada.configuracao.ultimaExecucaoStatus === 'SUCESSO' && selecionada.configuracao.ultimaExecucaoMensagem && (
              <span className="text-amber-600"> · {selecionada.configuracao.ultimaExecucaoMensagem}</span>
            )}
          </div>
        </Card>
      )}

      <AlertDialog open={confirmar !== null} onOpenChange={(open) => !open && setConfirmar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmar === 'limpeza' ? 'Apagar mídias vencidas agora?' : 'Unificar arquivos duplicados?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmar === 'limpeza' ? (
                <>
                  As mídias de <strong>{selecionada?.empresa.nome}</strong> mais antigas que os prazos salvos ({selecionada && resumoPrazos(selecionada.configuracao)})
                  serão apagadas do servidor. Não dá para desfazer. Rode "Simular" antes para ver quanto será removido.
                </>
              ) : (
                <>
                  Cópias idênticas de um mesmo arquivo passam a ocupar espaço uma vez só no disco. Nenhum arquivo some e nenhuma
                  mensagem é alterada. Pode levar alguns minutos em empresas com muitas mídias.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className={confirmar === 'limpeza' ? 'bg-red-600 hover:bg-red-700' : undefined}
              onClick={() => {
                if (confirmar) iniciar(confirmar);
                setConfirmar(null);
              }}
            >
              {confirmar === 'limpeza' ? 'Apagar mídias vencidas' : 'Unificar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RetencaoMidiasConfig;
